import { Box, Stack, Typography } from '@mui/joy';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { AgGridTable, Button, PageTitle } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider, SelectionCountProvider } from 'core/states';
import { useGetPartFinderCatalogFacets, useGetPartFinderCatalogFamilies } from 'partFinder/hooks';
import { EMPTY_SELECTION, usePartFinderReturn } from 'partFinder/utils';
import { Direction, Level, Size } from 'theme/enums';

import { CatalogContext } from './CatalogContext';
import { CatalogFilters } from './CatalogFilters';
import { getColumnDefs } from './columns';
import { AppliedFilters, IAppliedFilter } from '../AppliedFilters';
import { PartFinderFamilyDrawer } from '../PartFinderFamilySearch';

export const PartFinderCatalog = () => {
  const { t } = useTranslation();
  const { user } = useAuthUser();
  const { returnTo, cancel } = usePartFinderReturn();

  const [manufacturerIds, setManufacturerIds] = useState<number[]>([]);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [locationIds, setLocationIds] = useState<number[]>([]);
  const [inStock, setInStock] = useState(false);
  const [drawerFamilyId, setDrawerFamilyId] = useState<number | null>(null);

  const { records, isLoading, loadingPercentage, currentPage } = useGetPartFinderCatalogFamilies({
    inStock,
    locationIds,
  });
  const { data: facets } = useGetPartFinderCatalogFacets();

  const selection = useMemo(
    () => ({ ...EMPTY_SELECTION, inStock, locationIds, manufacturerIds }),
    [inStock, locationIds, manufacturerIds]
  );

  const columnDefs = useMemo(() => getColumnDefs(), []);

  const catalogContext = useMemo(
    () => ({ selection, returnTo, onOpenFamily: setDrawerFamilyId }),
    [selection, returnTo]
  );

  // A picked category takes in everything below it.
  const categoryIdsWithDescendants = useMemo(() => {
    const ids = new Set(categoryIds);
    let added = true;

    while (added) {
      added = false;

      (facets?.categories ?? []).forEach(({ id, parentId }) => {
        if (parentId !== null && ids.has(parentId) && !ids.has(id)) {
          ids.add(id);
          added = true;
        }
      });
    }

    return ids;
  }, [categoryIds, facets]);

  const families = useMemo(
    () =>
      (records ?? []).filter(
        (family) =>
          (!manufacturerIds.length ||
            family.manufacturers.some(({ id }) => manufacturerIds.includes(id))) &&
          (!categoryIds.length ||
            (family.categoryId !== null && categoryIdsWithDescendants.has(family.categoryId)))
      ),
    [records, manufacturerIds, categoryIds, categoryIdsWithDescendants]
  );

  const appliedFilters = useMemo<IAppliedFilter[]>(() => {
    const myLocationId = user?.location?.id ? Number(user.location.id) : null;

    return [
      ...manufacturerIds.map((id) => ({
        key: `manufacturer${id}`,
        label: facets?.manufacturers.find((item) => item.id === id)?.name ?? String(id),
        onRemove: () => setManufacturerIds((ids) => ids.filter((value) => value !== id)),
      })),
      ...categoryIds.map((id) => ({
        key: `category${id}`,
        label: facets?.categories.find((item) => item.id === id)?.name ?? String(id),
        onRemove: () => setCategoryIds((ids) => ids.filter((value) => value !== id)),
      })),
      ...(inStock
        ? [{ key: 'inStock', label: t('labelInStock'), onRemove: () => setInStock(false) }]
        : []),
      ...locationIds.map((id) => ({
        key: `location${id}`,
        label:
          id === myLocationId
            ? t('labelMyLocation')
            : facets?.locations?.find((item) => item.id === id)?.name ?? String(id),
        onRemove: () => setLocationIds((ids) => ids.filter((value) => value !== id)),
      })),
    ];
  }, [manufacturerIds, categoryIds, inStock, locationIds, facets, user, t]);

  const resetAll = () => {
    setManufacturerIds([]);
    setCategoryIds([]);
    setLocationIds([]);
    setInStock(false);
  };

  return (
    <>
      <PageTitle
        title={t('titlePartFinderCatalog')}
        subtitle={t('subtitlePartFinderCatalog')}
        buttons={
          returnTo
            ? [
                <Button
                  key="cancel"
                  text={t('buttonLabelCancel')}
                  onClick={cancel}
                  size={Size.MD}
                />,
              ]
            : undefined
        }
      />

      <Stack direction={Direction.ROW} sx={{ flex: 1, minHeight: 0, gap: 2 }}>
        <CatalogFilters
          facets={facets}
          manufacturerIds={manufacturerIds}
          categoryIds={categoryIds}
          locationIds={locationIds}
          inStock={inStock}
          onManufacturerIdsChange={setManufacturerIds}
          onCategoryIdsChange={setCategoryIds}
          onLocationIdsChange={setLocationIds}
          onInStockChange={setInStock}
        />

        <Stack sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          <Typography level={Level.BODY_SM} sx={{ mb: 1 }}>
            {t('labelPartFinderAssetsCount', { count: families.length })}
          </Typography>
          <Box sx={{ mb: 1 }}>
            <AppliedFilters filters={appliedFilters} onResetAll={resetAll} />
          </Box>

          <Box sx={{ flex: 1, minHeight: 0 }}>
            <CatalogContext.Provider value={catalogContext}>
              <SelectionCountProvider>
                <AgGridClientTableProvider
                  tableId="partFinderCatalog"
                  entityType="Family"
                  columnDefs={columnDefs}
                  data={families}
                  isDataLoading={isLoading}
                  loadingPercentage={loadingPercentage}
                  dataCurrentPage={currentPage}
                  layoutType={TableLayoutType.PART_FINDER_CATALOG}
                  hasLayoutActions
                >
                  <AgGridTable />
                </AgGridClientTableProvider>
              </SelectionCountProvider>
            </CatalogContext.Provider>
          </Box>
        </Stack>
      </Stack>

      <PartFinderFamilyDrawer
        familyId={drawerFamilyId}
        initialSelection={selection}
        onClose={() => setDrawerFamilyId(null)}
      />
    </>
  );
};
